import * as Sentry from "@sentry/react";
import { useRef, useState, useEffect } from "react";
import styled from "styled-components/macro";
import SVG from "react-inlinesvg";
import { useStructuredOutlineStore } from "../StructuredOutlineStore";
import useOnClickOutside from "../../../../hooks/useOnClickOutside";
import { useProjectStore } from "../../../../stores/projectStore";
import { useStoryBibleStore } from "../../../../stores/storyBibleStore";
import classNames from "classnames";
import { useSeriesStore } from "../../../../stores/seriesStore";
import { useToast } from "../../../ToastWrapper";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { AppEventPublisher } from "../../../../AppEventPublisher";
import { PROSE_MODES } from "../..";
import { useStickyTourStore } from "../../../../stores/stickyTourStore";
import { AnchorIds, TourSignal } from "../../../StickyTour/TourTypes";
import StickyNoteAnchor from "../../../StickyTour/StickyNoteAnchor";

export const CREATE_DOCUMENT_DISPLAY_MODES = {
  followsDocument: "followsDocument",
  followsOutline: "followsOutline",
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  background-color: var(--menu-color-fullscreen);
  border-radius: 20px;
  box-shadow: var(--tiny-drop-shadow-head-on);
  margin-bottom: 20px;
  margin-top: 20px;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;

  color: var(--grey-0);

  h1 {
    font-size: 20px;
    color: var(--grey-0);
    font-weight: 400;
  }
`;

const ButtonContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: var(--primary-cta);
  color: var(--cta-text-color);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  height: 40px;
  svg {
    padding: 10px;
    margin: 0 !important;
    path {
      fill: var(--cta-text-color);
    }
    margin-right: 5px;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const ButtonText = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 12px;
  justify-content: center;

  color: var(--cta-text-color);
  h1 {
    margin: 0 !important;
    font-size: 14px;
    color: var(--cta-text-color) !important;
    font-weight: 500;
    line-height: 100%;
  }
  h2 {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 10px !important;
    color: var(--cta-text-color) !important;
    font-weight: 600;
    letter-spacing: 0.5px;
    line-height: 130%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 120px;
  }
`;

const ChevronContainer = styled.div`
  border-left: 1px solid var(--primary-cta-divider);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 6px;
  padding-right: 9px;
  svg {
    margin-right: 0px !important;
    padding: 0;
  }
`;

const PickerContainer = styled.div`
  z-index: 9999;
  position: absolute;
  top: 51px;
  right: 0px;
  width: 250px;
  max-height: 200px;
  overflow-y: scroll;
  overflow-x: hidden;
  background-color: var(--menu-color-fullscreen);
  color: var(--grey-4);
  border-radius: 12px;
  box-shadow: var(--drop-shadow-15-25);
  padding: 4px;
  display: flex;
  flex-direction: column;
  padding-right: 0;

  .option {
    /* width: calc(100% - 10px); */
    display: flex;
    align-items: center;
    cursor: pointer;
    min-height: 40px;
    height: 40px;
    padding-left: 10px;
    padding-right: 10px;
    border-radius: 10px;
    transition: background-color 0.2s;
    color: var(--grey-0);

    &:hover {
      background-color: var(--menu-color-hover);
    }
    white-space: nowrap;
    /* overflow: hidden; */
    text-overflow: ellipsis;
    height: 40px;
    font-weight: 400;
  }
`;

function CreateDocument({ project, setDocumentId, documentId, mode }) {
  const outline = useStructuredOutlineStore((state) => state.outline);
  const [newDocumentId, setNewDocumentId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [userHasSelected, setUserHasSelected] = useState(false);
  const createPOVTenseForDocument = useProjectStore((state) => state.createPOVTenseForDocument);
  const povTense = useProjectStore((state) => state.povTense);
  const creatingDocument = useProjectStore((state) => state.creatingDocument);
  const setCreatingDocument = useProjectStore((state) => state.setCreatingDocument);
  const { setToast } = useToast();
  const createStructureOutlineScene = useStoryBibleStore((state) => state.createStructureOutlineScene);
  const scenes = useStoryBibleStore((state) => state.scenes);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const storyBibleSaved = useStoryBibleStore((state) => state.saved);
  const setSceneId = useStoryBibleStore((state) => state.setSceneId);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const hasAnyChapterContent = useStructuredOutlineStore((state) => state.hasAnyChapterContent);
  const buildChapterOptions = useStructuredOutlineStore((state) => state.buildChapterOptions);
  const getSceneForDocument = useStoryBibleStore((state) => state.getSceneForDocument);
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);
  const subscription = useSudowriteStore((state) => state.subscription);
  const getDraftDefaults = useSudowriteStore((state) => state.getDraftDefaults);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  const pickerRef = useRef(null);
  useOnClickOutside(pickerRef, () => setPickerOpen(false));

  useEffect(() => {
    setSelectedItem(null);
    setUserHasSelected(false);
  }, [documentId]);

  const chapterOptions = outline?.content?.items.filter((item) => item.type === "chapter");
  const connectedScene = getSceneForDocument(documentId);
  const connectedChapterIndex = chapterOptions?.findIndex((item) => item.id === connectedScene?.itemId);
  const nextChapterIndex = chapterOptions?.findIndex((item, index) => index > connectedChapterIndex && item.type === "chapter");
  useEffect(() => {
    if (mode === CREATE_DOCUMENT_DISPLAY_MODES.followsOutline && !selectedItem && chapterOptions?.length > 0) {
      // in this view, we always default to first chapter.
      setSelectedItem(chapterOptions[0]);
      return;
    }
    if (mode === CREATE_DOCUMENT_DISPLAY_MODES.followsOutline) {
      // the item is already set and in this mode, nothing to do.
      return;
    }
    if (chapterOptions?.length === 0) {
      // no chapters to select from
      return;
    }
    if (connectedChapterIndex > -1 && nextChapterIndex > -1) {
      if (selectedItem?.id === chapterOptions[nextChapterIndex].id || userHasSelected) {
        // already selected
        return;
      }
      setSelectedItem(chapterOptions[nextChapterIndex]);
      return;
    }
    // nothing to select yet
  }, [
    //
    outline,
    setSelectedItem,
    selectedItem,
    chapterOptions,
    documentId,
    getSceneForDocument,
    connectedScene,
    connectedChapterIndex,
    nextChapterIndex,
    mode,
  ]);

  const handlePicker = (e) => {
    e.stopPropagation();
    setPickerOpen(!pickerOpen);
  };

  const handleAdd = async () => {
    if (creatingDocument) return;
    if (!selectedItem?.id) {
      setToast("Please select a chapter to create a document for.");
      return;
    }
    setCreatingDocument(true);

    try {
      const chapters = buildChapterOptions();
      const previousDocumentId = subscription?.hasAccessToChapterGlueBeta
        ? await project.getPreviousDocumentIdFromChapter({
            chapterId: selectedItem.itemId,
            chapters,
            scenes,
            userId: subscription.id,
          })
        : null;
      const result = await project.createDocument({
        title: selectedItem.name,
        previousDocumentId,
        ...(subscription?.hasAccessToPOVAndTenseBeta && selectedItem && selectedItem.povTenseId ? { povTenseId: selectedItem.povTenseId } : {}),
      });
      if (!result?.document?.id || !selectedItem?.id) {
        setCreatingDocument(false);
        setToast("Unable to create document.  Please try again.");
        return;
      }

      // Create a POV Tense for the document that uses the project POV and tense as the default values:
      // 1. If they exist, and
      // 2. If the outline item doesn't already have a POV Tense ID
      if (!selectedItem?.povTenseId && subscription?.hasAccessToPOVAndTenseBeta && povTense) {
        await createPOVTenseForDocument(project, result.document, povTense);
      }
      AppEventPublisher.storyBible.outline.addChapterDocument(mode === CREATE_DOCUMENT_DISPLAY_MODES.followsDocument ? "under-document" : "under-outline");
      const newSceneId = createStructureOutlineScene(result.document.id, selectedItem.id);
      sendFlowSignal(TourSignal.STORY_BIBLE_CREATE_DOCUMENT_CLICKED);

      const { modelKey, creativity } = getDraftDefaults(project?.id);
      if (modelKey || typeof creativity === "number") {
        setScenes((draft) => {
          if (!draft[newSceneId]) return;
          if (modelKey && PROSE_MODES?.[modelKey]) {
            draft[newSceneId].proseMode = modelKey;
          }
          if (typeof creativity === "number") {
            draft[newSceneId].creativityLevel = creativity;
          }
        });
      }
      setNewDocumentId(result.document.id);
    } catch (e) {
      Sentry.captureException(e, { tags: { projectId: project?.id || "unknown", feature: "structured_outline_create_document" } });
      setToast("Unable to create document.  Please try again.");
      setCreatingDocument(false);
    }
  };

  useEffect(() => {
    // This is used to wait until the story bible is saved before nagivating to the new document.
    try {
      if (!newDocumentId) return;
      if (!storyBibleSaved) return;
      const newScene = Object.values(scenes).find((i) => i?.contents?.id === newDocumentId);
      if (newScene) {
        // Ensure draft defaults are applied for the freshly created linked chapter (post-persist)
        try {
          const { modelKey, creativity } = getDraftDefaults(project?.id);
          if ((modelKey && !newScene.proseMode) || (typeof creativity === "number" && newScene.creativityLevel === undefined)) {
            setScenes((draft) => {
              const sceneRef = draft[newScene.id];
              if (!sceneRef) return;
              if (modelKey && PROSE_MODES?.[modelKey] && !sceneRef.proseMode) {
                sceneRef.proseMode = modelKey;
              }
              if (typeof creativity === "number" && sceneRef.creativityLevel === undefined) {
                sceneRef.creativityLevel = creativity;
              }
            });
          }
        } catch (err) {
          Sentry.captureException(err, { tags: { projectId: project?.id || "unknown", feature: "create_document_apply_defaults" } });
        }
        setSceneId(newScene.id);
        setDocumentId(newDocumentId);
        subscription?.hasAccessToChapterGlueBeta ? setProseGeneratorActive(false) : setProseGeneratorActive(true);
        setCreatingDocument(false);
        setNewDocumentId(null);
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId: project?.id || "unknown", feature: "structured_outline_create_document_navigation" } });
      setToast("Unable to create document.  Please try again.");
    }
  }, [newDocumentId, scenes, setDocumentId, setProseGeneratorActive, setSceneId, setCreatingDocument, storyBibleSaved]);

  const handleSelectItem = (item) => (e) => {
    // since this is mounted within the button for positioning purposes
    // all clicks would propagate to the button and close the picker
    // this uses a closure for better ergonomics
    e.stopPropagation();
    setUserHasSelected(true);
    setSelectedItem(item);
    setPickerOpen(false);
  };

  if (!hasAnyChapterContent()) {
    return null;
  }

  const titleLine = mode === CREATE_DOCUMENT_DISPLAY_MODES.followsDocument ? "Done with this chapter?" : "Now the fun part begins!";
  const ctaLanguage = mode === CREATE_DOCUMENT_DISPLAY_MODES.followsDocument ? "continue with the next chapter" : "ready to draft your Chapter";
  if (mode === CREATE_DOCUMENT_DISPLAY_MODES.followsDocument && nextChapterIndex === -1) {
    // There are no more chapters to follow.
    // Might be nice to do a congratulations message here.
    return null;
  }
  if (mode === CREATE_DOCUMENT_DISPLAY_MODES.followsDocument && !connectedScene?.itemId) {
    // do not display if the document is not connected to a scene.
    return null;
  }
  if (project.seriesId && selectedProjects.outline?.projectId !== project.id) {
    // do not display if the user is viewing a different project's outline within a series
    return null;
  }

  return (
    <Container>
      <TextContainer>
        <h1>{titleLine}</h1>
        <p>
          {mode === CREATE_DOCUMENT_DISPLAY_MODES.followsDocument ? (<>Click <strong>Start</strong> to&nbsp;{ctaLanguage}.</>) : (<>Click <strong>Start</strong> when you're&nbsp;{ctaLanguage}.</>)}
        </p>
      </TextContainer>
      <ButtonContainer
        onClick={handleAdd}
        role="button"
        aria-disabled={creatingDocument}
        className={classNames({ disabled: creatingDocument })}
        id="button-create-document"
      >
        <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_CREATE_DOCUMENT} />
        <SVG
          src="/assets/add.svg"
          height={20}
          width={20}
        />
        <ButtonText>
          <h1>Start</h1>
          <h2>{selectedItem?.name}</h2>
        </ButtonText>
        <ChevronContainer onClick={handlePicker}>
          <SVG
            src="/assets/chevron-down.svg"
            aria-hidden="true"
            height={24}
            width={24}
          />
        </ChevronContainer>
        {pickerOpen && (
          <PickerContainer ref={pickerRef}>
            {chapterOptions.map((chapter) => (
              <div
                className="option"
                key={chapter.id}
                onClick={handleSelectItem(chapter)} // note this is a thunk.
              >
                {chapter.name}
              </div>
            ))}
          </PickerContainer>
        )}
      </ButtonContainer>
    </Container>
  );
}

export default CreateDocument;
